import React, { useId } from "react";

/**
 * Logo mark visuals: real <img> + SVG filters for tint and effects.
 * Avoids CSS mask-image under a filtered ancestor (Chromium bug → logos vanish).
 */
const WatermarkLogoMarkMedia = ({
  logoUrl,
  tint = null,
  effect = "none",
  effectColor = "#FFFFFF",
  imgStyle = null,
  className = "watermark-preview__logo-img",
  borderRadius = undefined,
}) => {
  const uid = useId().replace(/:/g, "");
  const filterId = `wm-logo-fx-${uid}`;
  const fx = effect && effect !== "none" ? effect : "none";
  const needsFilter = Boolean(tint) || fx !== "none";

  const stroke =
    String(effectColor).toUpperCase() === "#FFFFFF" ||
    String(effectColor).toUpperCase() === "#FFF"
      ? "#111111"
      : "#ffffff";

  const { filter: _ignoreCssFilter, ...safeImgStyle } = imgStyle || {};

  const baseStyle = {
    borderRadius: borderRadius ?? "inherit",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    ...safeImgStyle,
    ...(needsFilter ? { filter: `url(#${filterId})` } : {}),
  };

  return (
    <>
      {needsFilter ? (
        <svg
          width="0"
          height="0"
          aria-hidden="true"
          focusable="false"
          style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
        >
          <defs>
            <filter
              id={filterId}
              x="-40%"
              y="-40%"
              width="180%"
              height="180%"
              colorInterpolationFilters="sRGB"
            >
              {tint ? (
                <>
                  <feFlood floodColor={tint} result="flood" />
                  <feComposite
                    in="flood"
                    in2="SourceAlpha"
                    operator="in"
                    result="base"
                  />
                </>
              ) : (
                <feOffset in="SourceGraphic" dx="0" dy="0" result="base" />
              )}

              {fx === "shadow" ? (
                <feDropShadow
                  in="base"
                  dx="2"
                  dy="2"
                  stdDeviation="3"
                  floodColor="rgba(0,0,0,0.55)"
                />
              ) : null}

              {fx === "glow" ? (
                <>
                  <feDropShadow
                    in="base"
                    dx="0"
                    dy="0"
                    stdDeviation="2.5"
                    floodColor={effectColor}
                    result="g1"
                  />
                  <feDropShadow
                    in="base"
                    dx="0"
                    dy="0"
                    stdDeviation="7"
                    floodColor={effectColor}
                    result="g2"
                  />
                  <feMerge>
                    <feMergeNode in="g2" />
                    <feMergeNode in="g1" />
                    <feMergeNode in="base" />
                  </feMerge>
                </>
              ) : null}

              {fx === "outline" ? (
                <>
                  <feMorphology
                    in="base"
                    operator="dilate"
                    radius="1.4"
                    result="dilated"
                  />
                  <feFlood floodColor={stroke} result="outlineFlood" />
                  <feComposite
                    in="outlineFlood"
                    in2="dilated"
                    operator="in"
                    result="outline"
                  />
                  <feMerge>
                    <feMergeNode in="outline" />
                    <feMergeNode in="base" />
                  </feMerge>
                </>
              ) : null}
            </filter>
          </defs>
        </svg>
      ) : null}
      <img
        src={logoUrl}
        alt=""
        draggable={false}
        className={className}
        style={baseStyle}
      />
    </>
  );
};

export default WatermarkLogoMarkMedia;
